import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import { getEstimatedGroupPotCoins } from '../../../src/worldcup/helpers/worldCupGroupsDisplay.helper';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { GROUPS_VIEW_COPY } from './GroupsView.constants';
import type { getGroupsViewCardStyles } from './GroupsViewCard.styles';
import type { WorldCupGroup } from '../../../src/worldcup/types/worldcup.types';

const coinFormatter = new Intl.NumberFormat();

interface GroupsViewCardPrizeProps {
  group: WorldCupGroup;
  styles: ReturnType<typeof getGroupsViewCardStyles>;
}

/**
 * Card prize block.
 * - Company squads advertise their real top prize (voucher, tickets, …) — the
 *   coin pot is just entryFee × members and undersells the actual reward. When
 *   no rewards are configured we hide the block rather than show that pot.
 * - Regular squads show the coin prize pool.
 */
export function GroupsViewCardPrize({ group, styles }: GroupsViewCardPrizeProps) {
  if (group.companyBranding) {
    const topPrize = group.companyBranding.rewards?.find((r) => r.place === 1);
    if (!topPrize) return null;
    return (
      <View style={styles.prizeBlock}>
        <Text style={styles.prizeLabel}>{GROUPS_VIEW_COPY.TOP_PRIZE_LABEL}</Text>
        <View style={styles.topPrizeRow}>
          {topPrize.imageUrl ? (
            <Image
              source={{ uri: topPrize.imageUrl }}
              style={styles.topPrizeImage}
              contentFit="cover"
              alt={topPrize.label}
            />
          ) : (
            <Text style={styles.topPrizeMedal}>🥇</Text>
          )}
          <Text style={styles.topPrizeLabel} numberOfLines={2}>
            {topPrize.label}
          </Text>
        </View>
      </View>
    );
  }

  const pot = getEstimatedGroupPotCoins(group.entryFeeCoins ?? 0, group.memberCount);
  return (
    <View style={styles.prizeBlock}>
      <Text style={styles.prizeLabel}>{GROUPS_VIEW_COPY.PRIZE_POOL_LABEL}</Text>
      <View style={styles.prizeValueRow}>
        <Image
          source={IDLE_COIN_SOURCE}
          style={styles.coinIconSmall}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
        <Text style={styles.prizeValue}>{coinFormatter.format(pot)}</Text>
      </View>
    </View>
  );
}
